import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import FieldRow from 'components/FormElements/FieldRow';
import TextField from 'components/FormElements/AppTextInput';
import { FirmBankDetailsSelector } from 'components/FirmBankDetailsSelector';
import { createFirmEditRoute } from 'adminConstants/routes';
import RecordView from 'components/RecordView';
import CkEditor from 'components/CkEditor';

const useStyles = makeStyles()(() => ({
  marginLeftBtn: {
    marginLeft: 10,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  switchLabel: {
    color: '#656565',
    fontSize: 12,
    margin: '0 10px',

    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  switchNote: {
    color: '#656565',
    fontSize: 12,
    marginLeft: 16,
  },
}));

type Props = any;

const BankDetails: React.FC<Props> = ({
  bank,
  bankError,
  handleCancel,
  handleClickBank,
  id,
  setBank,
  firmId,
  buttonDisabled = false,
  formDisabled = false,
}) => {
  const { classes } = useStyles();

  return (
    <RecordView>
      <FieldRow
        title="Select payment account"
        centerTitle
        tooltipText={
          <span>
            To add a new bank account, navigate to the{' '}
            <em>Fund bank account details</em> section of your{' '}
            <Link to={createFirmEditRoute(firmId)}>My Firm</Link> page.
          </span>
        }
      >
        <FirmBankDetailsSelector
          firmId={firmId}
          onChange={(firmBankDetailId) =>
            setBank({ ...bank, firmBankDetailId })
          }
          placeholder="Select payment account"
          currentValue={bank.firmBankDetailId}
          disabled={formDisabled}
        />
        {bankError?.firmBankDetailId && (
          <span style={{ color: 'red' }}>{bankError.firmBankDetailId}</span>
        )}
      </FieldRow>

      <FieldRow
        title="Custom reference"
        tooltipText="By default, the system automatically generates a unique bank transfer reference for each investor. If you want investors to follow a specific format instead, please input this here for investors to see alongside your bank account details. Please note that if any text is input here, it will override the system’s automatic payment reference creation feature."
        centerTitle
      >
        <TextField
          name="customBankTransferReference"
          fullWidth
          disabled={formDisabled}
          onChange={(event) =>
            setBank({
              ...bank,
              customBankTransferReference: event.target.value,
            })
          }
          value={bank.customBankTransferReference}
          placeholder="e.g. AV-FirstNameLastName"
          variant="outlined"
        />
      </FieldRow>

      <FieldRow
        title="Payment helper text"
        informationText="This helper text appears on the bank transfer payment page, alongside bank details."
        topTitle
      >
        <CkEditor
          placeholder="(Optional) Enter up to 30-35 words in this field if you wish to provide additional payment instructions or context, for example how to pay by cheque, or what your custodian’s name is. The system automatically creates payment references for each investor, so please do not add references here."
          onChange={(value: string) => {
            setBank({ ...bank, title: value });
          }}
          value={bank?.title}
          disabled={formDisabled}
          onBlur={(value: string) => {
            setBank({ ...bank, title: value });
          }}
          extraLines={1}
        />
      </FieldRow>

      {!formDisabled && (
        <FieldRow>
          <div className={classes.buttonContainer}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              className={classes.marginLeftBtn}
              variant="contained"
              color="primary"
              disabled={buttonDisabled}
              onClick={handleClickBank}
            >
              {id ? 'Update' : 'Save'}
            </Button>
          </div>
        </FieldRow>
      )}
    </RecordView>
  );
};

export default BankDetails;
